import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { get } from "lodash";

import { queryClient } from "App";
import tagConstants from "constants/Tag";
import EntityService from "services/Entity";
import UserService from "services/User";
import TagService from "services/Tag";
import CustomFieldService from "services/CustomField";
import {
  getEntityTableColumns,
  getEntityTableFilters,
} from "routes/entity/components/entity_table/EntityTableColumnsAndFilters";
import { ENTITIES_TABLE_CONSTANTS } from "routes/entity/components/entity_table/EntitiesTableV3";
import { buildCustomFieldFilters } from "components/BaseTableV3/table-helpers";
import { addCustomFieldsColumns, addResizedColumns } from "./useTableConfig";
import { useRegionCountryForFilters } from "./useRegionCountryForFilters";

export const invalidateEntitiesQuery = async () => {
  await queryClient.invalidateQueries({
    queryKey: [ENTITIES_TABLE_CONSTANTS.queryKey],
  });
};

export const deleteEntity = async id => {
  await EntityService.deleteEntity(id);
  await invalidateEntitiesQuery();
};

const handleFetchCustomFields = async () => {
  const customFields = await EntityService.getCustomFields();
  const configs =
    await CustomFieldService.getCustomFieldConfigsForModel("Entity");
  return [
    {
      accessorPrefix: "custom_fields",
      headerPrefix: "Entity",
      fields: customFields,
      model: "Entity",
      configs,
    },
  ];
};

const handleFetchTags = async className => {
  const taggableModels = tagConstants["taggableModels"];
  const model =
    className === "EmailScheduleContacts"
      ? "contact"
      : Object.keys(taggableModels).find(
          key => taggableModels[key] === className
        );
  const tags = await TagService.getTagOptions({ model });

  return tags;
};

export const useEntityTable = (className = "Entity") => {
  const { isRegionsAndCountriesFetched, regionsAndCountries } =
    useRegionCountryForFilters();

  const { data: tags, isSuccess: isTagsFetched } = useQuery({
    queryKey: ["tags", "options", className],
    queryFn: () => handleFetchTags(className),
  });

  const { data: customFields, isSuccess: isCustomFieldsFetched } = useQuery({
    queryKey: ["custom-fields", className],
    queryFn: handleFetchCustomFields,
  });

  const { data: tableSettings, isSuccess: isTableSettingsFetched } = useQuery({
    queryKey: ["table-settings"],
    queryFn: UserService.getUserSettings,
  });

  const filters = useMemo(() => {
    if (
      !isRegionsAndCountriesFetched ||
      !isTagsFetched ||
      !isCustomFieldsFetched
    ) {
      return [];
    }

    const finalFilters = getEntityTableFilters({
      regions: regionsAndCountries?.regions,
      countries: regionsAndCountries?.countries,
      tags,
    });

    if (isCustomFieldsFetched) {
      finalFilters.push(
        ...buildCustomFieldFilters(
          customFields,
          finalFilters.map(f => f.title)
        )
      );
    }

    return finalFilters;
  }, [
    regionsAndCountries,
    customFields,
    tags,
    isRegionsAndCountriesFetched,
    isTagsFetched,
    isCustomFieldsFetched,
  ]);

  const columns = useMemo(() => {
    if (!isTableSettingsFetched || !isCustomFieldsFetched) return [];
    const initialColumns = getEntityTableColumns();
    const entityTableSettings = get(
      tableSettings,
      `user_settings.preferences.tables.${ENTITIES_TABLE_CONSTANTS.tableName}`,
      {}
    );

    const withCustomFieldsColumns = addCustomFieldsColumns({
      columns: initialColumns,
      customFields,
      visibleColumns: entityTableSettings?.visible_columns || [],
    });
    const withResizedColumns = addResizedColumns({
      columns: withCustomFieldsColumns,
      resizedColumns: entityTableSettings?.resized_columns || {},
    });

    return withResizedColumns;
  }, [
    customFields,
    tableSettings,
    isTableSettingsFetched,
    isCustomFieldsFetched,
  ]);

  return {
    filters,
    columns,
    tags,
    customFields,
  };
};
